import {
  CRITERION_TIER_LABELS,
  FUNNEL_STAGE_LABELS,
  FUNNEL_STAGE_MEANINGS,
  QUALIFICATION_VALUE_STATUS_LABELS,
  type CriterionState,
  type CriterionTier,
  type FunnelStage,
  type QualificationEvaluation,
  type QualificationValueStatus,
} from '@lia/contracts';
import { HelpCircleIcon } from '@hugeicons/core-free-icons';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatDateTime } from '../../lib/format.js';
import { Badge, StatusBadge, type BadgeTone, type StatusTone } from '../primitives/badge.js';
import { Icon } from '../primitives/icon.js';

// Setter funnel (fixed stages) and per-project qualification criteria (spec §05.1, §04). Presentational only.

export const FUNNEL_STAGE_TONE: Record<FunnelStage, StatusTone> = {
  assigned: 'neutral',
  contacted: 'info',
  conversation_open: 'info',
  qualified: 'accent',
  appointment_scheduled: 'success',
  appointment_completed: 'success',
  no_show: 'warning',
  appointment_cancelled: 'warning',
  appointment_rescheduled: 'info',
  future_potential: 'accent',
  lost: 'danger',
};

export const QUALIFICATION_STATUS_TONE: Record<QualificationValueStatus, StatusTone> = {
  confirmed: 'success',
  declared: 'warning',
  pending: 'neutral',
  inferred: 'info',
};

export const CRITERION_TIER_TONE: Record<CriterionTier, BadgeTone> = {
  mandatory: 'accent',
  desirable: 'outline',
  informative: 'neutral',
};

const SOURCE_LABELS: Record<string, string> = {
  ai: 'IA',
  advisor: 'Asesor',
  crm: 'CRM',
  form: 'Formulario',
  import: 'Importación',
  copilot: 'Copiloto',
};

/** Spanish label for a qualification value source (unknown keys are shown as-is). */
export function qualificationSourceLabel(source: string | null | undefined): string | null {
  if (!source) return null;
  return SOURCE_LABELS[source] ?? source;
}

export interface FunnelStageBadgeProps {
  stage: FunnelStage;
  /** Also render the fixed commercial meaning as visible text below the badge. */
  showMeaning?: boolean;
  className?: string;
}

/** Fixed funnel stage: tone + icon + Spanish label; the fixed meaning is the hover hint (and optional text). */
export function FunnelStageBadge({ stage, showMeaning, className }: FunnelStageBadgeProps) {
  const badge = (
    <StatusBadge tone={FUNNEL_STAGE_TONE[stage]} title={FUNNEL_STAGE_MEANINGS[stage]} className={showMeaning ? undefined : className}>
      {FUNNEL_STAGE_LABELS[stage]}
    </StatusBadge>
  );
  if (!showMeaning) return badge;
  return (
    <span className={cn('grid justify-items-start gap-1', className)}>
      {badge}
      <span className="text-meta text-text-secondary">{FUNNEL_STAGE_MEANINGS[stage]}</span>
    </span>
  );
}

/** Qualification value status chip (Confirmado / Declarado (sin confirmar) / Pendiente / Inferido). */
export function QualificationStatusBadge({ status }: { status: QualificationValueStatus }) {
  return <StatusBadge tone={QUALIFICATION_STATUS_TONE[status]}>{QUALIFICATION_VALUE_STATUS_LABELS[status]}</StatusBadge>;
}

export interface QualificationCriteriaListProps extends Pick<QualificationEvaluation, 'qualified' | 'pendingMandatory' | 'allowBookingWithPending'> {
  criteria: readonly CriterionState[];
  /** IANA timezone for dates (workspace timezone). */
  timezone: string;
  /** Per-criterion action (e.g. "Registrar valor"). */
  renderAction?: (c: CriterionState) => React.ReactNode;
  title?: string;
  className?: string;
}

/**
 * Per-project qualification: summary (Calificado or the pending mandatory criteria), and each criterion with tier,
 * current value, status, source and its value history (applied or kept only as history).
 */
export function QualificationCriteriaList({
  criteria,
  qualified,
  pendingMandatory,
  allowBookingWithPending,
  timezone,
  renderAction,
  title = 'Calificación',
  className,
}: QualificationCriteriaListProps) {
  const titleId = React.useId();
  const pendingId = React.useId();
  return (
    <section aria-labelledby={titleId} className={cn('grid gap-3', className)}>
      <h3 id={titleId} className="text-body-lg font-semibold text-text-primary">
        {title}
      </h3>
      <div className="grid gap-1.5 rounded-card border border-border-subtle bg-surface-subtle p-3">
        {qualified ? (
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone="success">Calificado</StatusBadge>
            <span className="text-meta text-text-secondary">Todos los criterios obligatorios están confirmados en conversación.</span>
          </div>
        ) : (
          <>
            <div>
              <StatusBadge tone="warning">Faltan criterios obligatorios</StatusBadge>
            </div>
            {pendingMandatory.length ? (
              <ul aria-label="Criterios obligatorios pendientes" id={pendingId} className="grid gap-0.5 text-body text-text-primary">
                {pendingMandatory.map((p) => (
                  <li key={p.key}>
                    {p.label} <span className="text-meta text-text-secondary">· {QUALIFICATION_VALUE_STATUS_LABELS[p.status]}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {allowBookingWithPending ? (
              <p className="text-meta text-text-secondary">
                Este proyecto permite agendar con criterios pendientes: se puede agendar, pero no queda como Calificado hasta confirmarlos.
              </p>
            ) : null}
          </>
        )}
      </div>
      {criteria.length === 0 ? (
        <p className="text-body text-text-secondary">El proyecto no tiene criterios de calificación.</p>
      ) : (
        <ul className="grid divide-y divide-border-subtle">
          {criteria.map((c) => (
            <li key={c.key} data-criterion={c.key} className="grid gap-1.5 py-2.5 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="text-meta font-medium text-text-secondary">{c.label}</span>
                  <Badge tone={CRITERION_TIER_TONE[c.tier]}>{CRITERION_TIER_LABELS[c.tier]}</Badge>
                  {c.custom ? <Badge tone="outline">Personalizado</Badge> : null}
                </span>
                {renderAction ? renderAction(c) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {c.value === null || c.value === '' ? (
                  <span className="inline-flex items-center gap-1 text-body italic text-text-secondary">
                    <Icon icon={HelpCircleIcon} aria-hidden className="size-4" />
                    Desconocido
                  </span>
                ) : (
                  <span className="text-body text-text-primary [overflow-wrap:anywhere]">{c.value}</span>
                )}
                <QualificationStatusBadge status={c.status} />
              </div>
              {c.source || c.at ? (
                <span className="flex flex-wrap gap-x-3 text-meta text-text-secondary">
                  {c.source ? <span>Fuente: {qualificationSourceLabel(c.source)}</span> : null}
                  {c.at ? <time dateTime={c.at}>{formatDateTime(c.at, timezone)}</time> : null}
                </span>
              ) : null}
              {c.history.length ? (
                <details>
                  <summary className="cursor-pointer rounded-control text-meta font-medium text-brand-primary focus-visible:outline-2 focus-visible:outline-focus">
                    Historial ({c.history.length})
                  </summary>
                  <ol aria-label={`Historial de ${c.label}`} className="mt-1.5 grid gap-2 border-l-2 border-border-subtle pl-3">
                    {c.history.map((h) => (
                      <li key={h.id} className="grid gap-0.5">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="text-body text-text-primary [overflow-wrap:anywhere]">{h.value}</span>
                          <QualificationStatusBadge status={h.status} />
                          <Badge tone={h.applied ? 'neutral' : 'outline'}>{h.applied ? 'Aplicado' : 'Solo historial'}</Badge>
                        </span>
                        <span className="flex flex-wrap gap-x-3 text-meta text-text-secondary">
                          <span>Fuente: {qualificationSourceLabel(h.source)}</span>
                          {h.actorName ? <span>Por: {h.actorName}</span> : null}
                          <time dateTime={h.recordedAt}>{formatDateTime(h.recordedAt, timezone)}</time>
                        </span>
                      </li>
                    ))}
                  </ol>
                </details>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
